import type { Table } from '@tanstack/react-table';
import { getHeaderNames } from 'tanstack-table-export-to-csv';

import { getPatientName } from '../../../uploader/patientList/patientListTableContainer/utils';

type SkipExportMeta = { skipExport?: boolean };
type ExportColumnMeta = SkipExportMeta & { isPatientName?: boolean };

/** RFC 4180: quote field if it contains comma, newline, or "; escape " as "". */
function escapeCsvField(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[,"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Build one CSV line from an array of values. */
const toCsvLine = (values: unknown[]) => values.map(escapeCsvField).join(',');

export type ExportTableToCsvOptions = {
  /** When true, patient name columns (meta.isPatientName) are exported as e.g. F**** L**** */
  isPrivacyOn?: boolean;
};

/**
 * Exports the current (filtered/sorted) table data to a CSV file and triggers download.
 * Columns with meta.skipExport: true are excluded. Values are escaped per RFC 4180.
 * When isPrivacyOn is true, columns with meta.isPatientName export masked names (F**** L****).
 */
export const exportTableToCsv = <TData>(
  table: Table<TData>,
  filename: string,
  options?: ExportTableToCsvOptions,
): void => {
  const headerGroups = table.getHeaderGroups();
  if (headerGroups.length === 0) return;

  const { isPrivacyOn = false } = options ?? {};
  const headers = headerGroups
    .flatMap((g) => g.headers)
    .filter(
      (h) =>
        !(h.column.columnDef.meta as SkipExportMeta | undefined)?.skipExport,
    );
  const rows = table.getRowModel().rows;

  const getCellValue = (row: (typeof rows)[0], header: (typeof headers)[0]) => {
    const value = row.getValue(header.column.id);
    const meta = header.column.columnDef.meta as ExportColumnMeta | undefined;
    if (isPrivacyOn && meta?.isPatientName && typeof value === 'string') {
      const name = value.trim();
      return name === '' ? '' : getPatientName(name, 0, false);
    }
    return value;
  };

  const csvContent = [
    toCsvLine(getHeaderNames(headers)),
    ...rows.map((row) => toCsvLine(headers.map((h) => getCellValue(row, h)))),
  ].join('\n');

  const url = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
